<template>
    <div class="default-channels-section">
        <v-expansion-panels flat v-model="expanded" multiple>
            <v-expansion-panel>
                <v-expansion-panel-header class="section-header">
                    <div class="section-header-content">
                        <h2 class="section-title">Каналы/каскады по умолчанию для триггеров и активаций</h2>
                    </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <div class="section-content">
                        <div class="default-channel-item">
                            <label class="channel-label">Сервисные уведомления:</label>
                            <div class="channel-select-wrapper">
                                <Select
                                    v-model="localChannels.notifications"
                                    :items="availableOptions"
                                    item-text="name"
                                    item-value="id"
                                    :selection-max-length="1000"
                                    dense
                                    hide-details
                                    class="channel-select"
                                    @input="updateChannels"
                                />
                            </div>
                        </div>
                        <div class="default-channel-item">
                            <label class="channel-label">Рассылки по базе клиентов:</label>
                            <div class="channel-select-wrapper">
                                <Select
                                    v-model="localChannels.mailings"
                                    :items="availableOptions"
                                    item-text="name"
                                    item-value="id"
                                    :selection-max-length="1000"
                                    dense
                                    hide-details
                                    class="channel-select"
                                    @input="updateChannels"
                                />
                            </div>
                        </div>
                        <div class="default-channel-item">
                            <label class="channel-label">Сообщения из CRM:</label>
                            <div class="channel-select-wrapper">
                                <Select
                                    v-model="localChannels.crmMessages"
                                    :items="availableOptions"
                                    item-text="name"
                                    item-value="id"
                                    :selection-max-length="1000"
                                    dense
                                    hide-details
                                    class="channel-select"
                                    @input="updateChannels"
                                />
                            </div>
                        </div>
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import Select from '@/components/inputs/Select.vue'

export default {
    name: 'DefaultChannelsSection',
    components: {
        Select,
    },
    props: {
        defaultChannels: {
            type: Object,
            required: true,
        },
        connectedChannels: {
            type: Array,
            required: true,
        },
        schemes: {
            type: Array,
            required: true,
        },
    },
    data: () => ({
        expanded: [0],
        localChannels: {
            notifications: null,
            mailings: null,
            crmMessages: null,
        },
    }),
    computed: {
        availableOptions() {
            const options = []
            
            // Добавляем все каналы
            this.connectedChannels.forEach(channel => {
                options.push({
                    id: `channel-${channel.id}`,
                    name: channel.name,
                    type: 'channel',
                    source: channel,
                })
            })
            
            // Добавляем все схемы
            this.schemes.forEach(scheme => {
                options.push({
                    id: `scheme-${scheme.id}`,
                    name: scheme.name,
                    type: 'scheme',
                    source: scheme,
                })
            })
            
            return options
        },
    },
    watch: {
        defaultChannels: {
            immediate: true,
            deep: true,
            handler(newVal) {
                this.localChannels = { ...newVal }
            },
        },
        connectedChannels: {
            immediate: true,
            deep: true,
            handler() {
                this.syncWithToggles()
            },
        },
        schemes: {
            immediate: true,
            deep: true,
            handler() {
                this.syncWithToggles()
            },
        },
    },
    methods: {
        updateChannels() {
            this.$emit('update', { ...this.localChannels })
            // Обновляем переключатели при изменении выпадающих списков
            this.updateTogglesFromSelects()
        },
        updateTogglesFromSelects() {
            // Обновляем переключатели на основе выбранных значений в выпадающих списках
            // Уведомления
            if (this.localChannels.notifications) {
                const parts = this.localChannels.notifications.split('-')
                if (parts.length >= 2) {
                    const type = parts[0]
                    const id = parts.slice(1).join('-') // Объединяем все части после первого дефиса
                    if (type === 'channel') {
                        this.$emit('select-default', { source: 'channel', sourceId: id, type: 'notifications', value: true })
                    } else if (type === 'scheme') {
                        this.$emit('select-default', { source: 'scheme', sourceId: id, type: 'notifications', value: true })
                    }
                }
            }
            
            // Рассылки
            if (this.localChannels.mailings) {
                const parts = this.localChannels.mailings.split('-')
                if (parts.length >= 2) {
                    const type = parts[0]
                    const id = parts.slice(1).join('-') // Объединяем все части после первого дефиса
                    if (type === 'channel') {
                        this.$emit('select-default', { source: 'channel', sourceId: id, type: 'mailings', value: true })
                    } else if (type === 'scheme') {
                        this.$emit('select-default', { source: 'scheme', sourceId: id, type: 'mailings', value: true })
                    }
                }
            }
            
            // CRM сообщения
            if (this.localChannels.crmMessages) {
                const parts = this.localChannels.crmMessages.split('-')
                if (parts.length >= 2) {
                    const type = parts[0]
                    const id = parts.slice(1).join('-') // Объединяем все части после первого дефиса
                    if (type === 'channel') {
                        this.$emit('select-default', { source: 'channel', sourceId: id, type: 'crmMessages', value: true })
                    } else if (type === 'scheme') {
                        this.$emit('select-default', { source: 'scheme', sourceId: id, type: 'crmMessages', value: true })
                    }
                }
            }
        },
        syncWithToggles() {
            // Синхронизируем выбранные значения с переключателями "по умолчанию"
            // Ищем канал или схему, у которых включен переключатель для уведомлений
            const notificationChannel = this.connectedChannels.find(ch => ch.defaultForNotifications)
            if (notificationChannel) {
                this.localChannels.notifications = `channel-${notificationChannel.id}`
            } else {
                const notificationScheme = this.schemes.find(s => s.defaultForNotifications)
                if (notificationScheme) {
                    this.localChannels.notifications = `scheme-${notificationScheme.id}`
                } else {
                    this.localChannels.notifications = null
                }
            }
            
            // Ищем канал или схему, у которых включен переключатель для рассылок
            const mailingChannel = this.connectedChannels.find(ch => ch.defaultForMailings)
            if (mailingChannel) {
                this.localChannels.mailings = `channel-${mailingChannel.id}`
            } else {
                const mailingScheme = this.schemes.find(s => s.defaultForMailings)
                if (mailingScheme) {
                    this.localChannels.mailings = `scheme-${mailingScheme.id}`
                } else {
                    this.localChannels.mailings = null
                }
            }
            
            // Ищем канал или схему, у которых включен переключатель для CRM сообщений
            const crmChannel = this.connectedChannels.find(ch => ch.defaultForCrmMessages)
            if (crmChannel) {
                this.localChannels.crmMessages = `channel-${crmChannel.id}`
            } else {
                const crmScheme = this.schemes.find(s => s.defaultForCrmMessages)
                if (crmScheme) {
                    this.localChannels.crmMessages = `scheme-${crmScheme.id}`
                } else {
                    this.localChannels.crmMessages = null
                }
            }
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.default-channels-section
    background: white
    border-radius: 8px
    padding: 16px

.section-header
    padding: 0 !important

.section-header-content
    display: flex
    justify-content: space-between
    align-items: center
    width: 100%

.section-title
    font-size: 18px
    font-weight: 500
    margin: 0

.section-content
    padding: 24px 0

.default-channel-item
    margin-bottom: 24px
    display: flex
    flex-direction: column

.channel-label
    font-size: 14px
    color: #2A3B51
    margin-bottom: 8px

.channel-select-wrapper
    display: flex
    align-items: center
    gap: 12px

.channel-select
    min-width: 300px
    flex: 1
    max-width: 100%

.yc-only-note
    font-size: 12px
    color: #F44336
</style>

